import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from './store/app.state';
import * as AppActions from './store/app/actions';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { PageHeaderService } from './shared/service/page-header/page-header.service';
import { BreadcrumbInterface, PageHeaderInterface } from './shared/service/page-header/page-header.model';
import { environment } from '../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import { User } from './store/user/model';
import { takeWhile } from 'rxjs/operators';
import * as MainActions from './store/main/main.actions';
import { BROWSER_TITLE_SUFFIX } from '../constants';
import * as UserConfigurationActions from './store/user-configuration/user-configuration.actions';
import { JWTTokenService } from './shared/service/jwt-token-service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'Iot Device Management Portal';
  private url: string;
  private refresherTimeout = null;
  private tenMinInMs = 600000;
  private isTokenLoaded: boolean = false;
  public jwtToken: string;

  private setTimeoutForSilentRefresh(): void {
    clearTimeout(this.refresherTimeout);
    this.refresherTimeout = setTimeout(() => {
      this.renewToken(false);
    }, this.tenMinInMs);
  }

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private toast: ToastrService,
    private pageHeaderService: PageHeaderService,
    private cookieService: CookieService,
    private http: HttpClient,
    private jwtTokenService: JWTTokenService,
  ) {
    this.url = `${environment.ssoUrl}${environment.ssoSilentAuthUrl}`;
    window.addEventListener('online', () => {
      window.location.reload();
      this.store.dispatch(new AppActions.HideMask());
    });

    window.addEventListener('offline', () => {
      this.store.dispatch(new AppActions.ShowMask());
      this.toast.warning(
        'No Internet Connection',
        'Connectivity',
        {
          closeButton: false,
          progressBar: false,
          disableTimeOut: true,
          tapToDismiss: false,
          positionClass: 'toast-bottom-right',
        },
      );
    });
  }

  async ngOnInit(): Promise<void> {

    this.store.dispatch(new MainActions.GetNavigationMenu());
    let token = this.jwtTokenService.getJwtCookie(environment.sessionTokenName);
    console.log(`is token expired : ${this.jwtTokenService.isTokenExpired()}`);

    if (token && !this.jwtTokenService.isTokenExpired()) {
      this.jwtToken = token;
    } else {
      this.jwtToken = await this.getTokenWithDelay();
    }
    if(this.jwtToken !== '') {
      this.store.dispatch(new UserConfigurationActions.GetUserConfigurationLoading());
    }

    this.store
      .select('user')
      .pipe(takeWhile(() => !this.isTokenLoaded))
      .subscribe((state: User) => {
        if (state.isTokenRefreshed) {
          this.isTokenLoaded = state.isTokenRefreshed;
          this.renewToken();
        }
      });
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      if (event instanceof NavigationEnd) {

      }

      window.scrollTo(0, 0);
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const configuration: PageHeaderInterface[] = this.getPageConfiguration(
          this.router.routerState,
          this.router.routerState.root,
        );

        if (configuration.length === 0 || configuration[0].title === undefined) {
          return;
        }

        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: BreadcrumbInterface[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);

        const pageConfig: PageHeaderInterface = configuration[0];
        const pageConfigTitle = pageConfig.title;
        this.titleService.setTitle(pageConfigTitle + BROWSER_TITLE_SUFFIX);
        this.setHeader({
          breadcrumbs,
          title: pageConfigTitle,
          titleKey: pageConfig.titleKey,
          icon: pageConfig.icon,
          setPageHeader: pageConfig.setPageHeader,
          fullScreenButton: pageConfig.fullScreenButton,
          fullScreenTargetElementId: pageConfig.fullScreenTargetElementId,
          isTabPage: pageConfig.isTabPage,
          hideHeader: pageConfig.hideHeader,
        });
      }
    });
  }

  private async getTokenWithDelay():Promise<string> {
    this.store.dispatch(new AppActions.ShowMask());
    await new Promise(r => setTimeout(r, 2000));
    this.store.dispatch(new AppActions.HideMask());
    return this.jwtTokenService.getJwtCookie(environment.sessionTokenName);
  }

  private getPageConfiguration(state, parent: ActivatedRoute): PageHeaderInterface[] {
    const data: PageHeaderInterface[] = [];
    if (parent && parent.snapshot.data && parent.snapshot.data.title) {
      data.push(<PageHeaderInterface>parent.snapshot.data);
    }

    if (state && parent) {
      data.push(...this.getPageConfiguration(state, state.firstChild(parent)));
    }

    return data;
  }

  private setHeader(config: PageHeaderInterface): void {
    let pageHeader: PageHeaderInterface = {
      title: config.title || null,
      titleKey: config.titleKey,
      icon: config.icon || null,
      fullScreenButton: config.fullScreenButton || false,
      fullScreenTargetElementId: config.fullScreenTargetElementId || null,
      breadcrumbs: config.breadcrumbs || [],
      isTabPage: config.isTabPage,
      hideHeader: config.hideHeader,
    };
    this.pageHeaderService.setHeader(pageHeader);
  }

  private renewToken(forTimeOutSetup: boolean = true): void {
    if (forTimeOutSetup) {
      this.setTimeoutForSilentRefresh();
      return;
    }

    this.jwtToken = this.jwtTokenService.getJwtCookie(environment.sessionTokenName);

    this.http
      .post<any>(this.url, {refresh_token: this.jwtToken})
      .pipe()
      .subscribe((resData: { success: boolean }) => {
        if (resData['success']) {
          this.jwtTokenService.setToken(resData['access_token']);
          this.setTimeoutForSilentRefresh();
        }
      });
  }

  private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrls: string[], breadcrumbs: any[]): void {
    if (route) {
      const routeUrls = parentUrls.concat(route.url.map((url: UrlSegment) => url.path));

      if (route.data.enableBreadcrumbs) {
        const routeUrlAsString = `/${routeUrls.join('/')}`;
        const breadcrumb: BreadcrumbInterface = {
          label: `pageTitles.${route.data.title}`,
          url: routeUrlAsString,
          disabled: this.router.url === routeUrlAsString,
        };
        breadcrumbs.push(breadcrumb);
      }

      this.addBreadcrumb(route.firstChild, routeUrls, breadcrumbs);
    }
  }
}
